/*  ========================================================================
    # Section - Hero Full
    ========================================================================  */

import React, { useRef, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import useSize from '@react-hook/size';
import LazyImage from '../../components/LazyImage';
import LazySource from '../../components/LazySource';
import bem from '../../helpers/bem';
import tailwindConfig from '../../../../tailwind.config';
import useLocalization from '../../hooks/useLocalization';
import useBreakpoints from '../../hooks/useBreakpoints';
import useTranslation from '../../hooks/useTranslation';
import useTabVisible from '../../hooks/useTabVisible';
import { useSwipeable } from 'react-swipeable';

const TeamOpinions = ({
  title,
  opinions
}) => {
  // useRef
  const sliderRef = useRef(null);
  const videoRef = useRef([]);

  // Determine if the tab is visible
  const tabVisible = useTabVisible();
  const __ = useTranslation();
  const { smUp, mdUp, lgUp, xlUp, xxlUp, mdDown } = useBreakpoints();
  const [sliderWidth] = useSize(sliderRef);
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeVideo, setActiveVideo] = useState(null);
  const [progress, setProgress] = useState(0.0);

  // Icons
  const localize = useLocalization();
  const iconArrowLeft = `${localize('svgSprite')}#arrow-left`;
  const iconArrowRight = `${localize('svgSprite')}#arrow-right`;
  const iconPlay = `${localize('svgSprite')}#play`;
  const iconStop = `${localize('svgSprite')}#stop`;

  // Set the visible slides corresponding to the current breakpoint.
  const visibleSlides = useMemo(() => {
    if (xxlUp) return 4;
    if (xlUp) return 4;
    if (lgUp) return 3;
    if (mdUp) return 2;
    if (smUp) return 2;
    return 1;
  });

  // Get Slide Offset
  const lastVisibleIndex = activeSlide + (visibleSlides - 1);
  const getSlideOffsetAttr = index => {
    if (index < activeSlide) return -1;
    if (index > lastVisibleIndex) return 1;
    return 0;
  };

  // Remove col gap from slider width for correct width based on 40px gap
  const calcSliderWidth = sliderWidth - (visibleSlides - 1) * 40;

  // Handle video change
  const handleActiveVideoChange = newVideoIndex => {
    // Pause current video if video changes
    if (newVideoIndex === activeVideo) {
      videoRef.current[activeVideo]?.pause();
      if (videoRef.current[activeVideo] !== null) {
        videoRef.current[activeVideo].currentTime = 0;
      }
      setActiveVideo(null);
      return;
    }
    if (newVideoIndex !== activeVideo) {
      videoRef.current[activeVideo]?.pause();
    }
    setActiveVideo(newVideoIndex);
  };

  // Check if active video is still in view after new activeSlide
  const isActiveVideoInViewport = newSlide =>
    activeVideo >= newSlide && activeVideo <= newSlide + (visibleSlides - 1);

  // Define max index depending on visible slides
  const maxIndex = opinions.length - visibleSlides;

  // Handle Slide
  const canNavigateLeft = activeSlide > 0;
  const handlePrevSlide = () => {
    if (canNavigateLeft) {
      const newActiveSlide = activeSlide - 1;
      setActiveSlide(newActiveSlide);

      // Change active video if active video slides out of viewport
      if (activeVideo !== null && !isActiveVideoInViewport(newActiveSlide)) {
        handleActiveVideoChange(newActiveSlide + (visibleSlides - 1));
      }
    }
  };

  const canNavigateRight = activeSlide < maxIndex;
  const handleNextSlide = () => {
    if (canNavigateRight) {
      const newActiveSlide = activeSlide + 1;
      setActiveSlide(newActiveSlide);

      // Change active video if active video slides out of viewport and not on mobile
      if (activeVideo !== null && !isActiveVideoInViewport(newActiveSlide) && !mdDown) {
        handleActiveVideoChange(newActiveSlide);
      }
    }
  };

  // Tick to increase the progress
  useEffect(() => {
    if (activeVideo !== null && tabVisible) {
      setProgress(0);
      videoRef.current[activeVideo]?.play();
    }
    // Pause video if tab is not visible
    if (!tabVisible && activeVideo !== null) {
      videoRef.current[activeVideo]?.pause();
    }
    const interval = setInterval(() => {
      if (activeVideo !== null && tabVisible) {
        // Determine progress by video duration. Subtracting 600ms to account for the fade-out animation
        const videoProgress =
          videoRef.current[activeVideo]?.currentTime / videoRef.current[activeVideo]?.duration || 0; // eslint-disable-line
        setProgress(Math.min(videoProgress, 1));
      }
    }, 50);
    return () => clearInterval(interval);
  }, [tabVisible, activeVideo]);

  // Add swipe handlers
  const swipeHandlers = useSwipeable({
    onSwipedRight: handlePrevSlide,
    onSwipedLeft: handleNextSlide
  });

  // Handle video when progress hits 100%
  useEffect(() => {
    if (progress >= 1.0) {
      // reset active video if last slide video played
      if (activeVideo === opinions.length - 1) {
        setActiveVideo(null);
        return;
      }
      // Got to next slide if next video play out of viewport
      if (!mdDown) {
        handleActiveVideoChange(activeVideo + 1);
      } else {
        handleActiveVideoChange(null);
      }
      if (activeVideo + 1 > lastVisibleIndex) {
        handleNextSlide();
      }
    }
  }, [progress]);

  return (
    <div className={bem('s-team-opinions', 'wrapper')}>
          <div className='container'>
            <div className={bem('s-team-opinions', 'header')}>
              {title && <div dangerouslySetInnerHTML={{ __html: title }}></div>}
              {opinions.length > 1 && (
                <div className={bem('s-team-opinions', 'controls')}>
                  <button
                    className={bem('c-button', '', {
                      'outline-black': true,
                      icon: true
                    })}
                    type='button'
                    title={__('slider-control-prev')}
                    aria-label={__('slider-control-prev')}
                    onClick={handlePrevSlide}
                    disabled={!canNavigateLeft}
                  >
                    <svg viewBox='0 0 200 200'>
                      <use href={iconArrowLeft}></use>
                    </svg>
                  </button>
                  <button
                    className={bem('c-button', '', {
                      'outline-black': true,
                      icon: true
                    })}
                    type='button'
                    title={__('slider-control-next')}
                    aria-label={__('slider-control-next')}
                    onClick={handleNextSlide}
                    disabled={!canNavigateRight}
                  >
                    <svg viewBox='0 0 200 200'>
                      <use href={iconArrowRight}></use>
                    </svg>
                  </button>
                </div>
              )}
            </div>
            <div className={bem('s-team-opinions', 'slider-wrapper')} ref={sliderRef}>
              <div
                className={bem('s-team-opinions', 'slider')}
                style={{
                  '--slider-width': `${calcSliderWidth}px`,
                  '--visible-slides': visibleSlides,
                  '--slide-offset': activeSlide
                }}
                {...swipeHandlers}
              >
                {opinions.map((opinion, index) => (
                  <div
                    key={index}
                    className={bem('s-team-opinions', 'slide', { play: index === activeVideo })}
                    data-offset={getSlideOffsetAttr(index)}
                  >
                    <div className={bem('s-team-opinions', 'slide-media-wrapper')}>
                      <div className={bem('s-team-opinions', 'slide-media')}>
                        <LazyImage
                          defaultUrl={opinion.image.sizes.medium}
                          placeholderUrl={opinion.image.sizes.placeholder}
                          figureClass={bem('s-team-opinions', 'slide-image')}
                          alt={title}
                          onClick={() => handleActiveVideoChange(index)}
                        >
                          <LazySource
                            srcSet={opinion.image.sizes.medium}
                            mediaMin={tailwindConfig.theme.screens.lg}
                          />
                        </LazyImage>
                        <picture
                          className={bem('s-team-opinions', 'slide-video')}
                          style={{
                            '--video-progress': 314 - progress * 314
                          }}
                        >
                          <video
                            src={opinion.video.url}
                            controls={false}
                            autoPlay={false}
                            playsInline={true}
                            ref={elem => (videoRef.current[index] = elem)}
                          >
                            {__('video-not-available')}
                          </video>
                          <svg
                            className={bem('s-team-opinions', 'slide-progress')}
                            viewBox='0 0 100 100'
                            version='1.1'
                          >
                            <circle
                              className={bem('s-team-opinions', 'slide-ring')}
                              cx='50'
                              cy='50'
                              r='50'
                            />
                          </svg>
                        </picture>

                        <button
                          className={bem('c-button', '', {
                            black: true,
                            icon: true
                          })}
                          type='button'
                          title={__('play-video-title')}
                          aria-label={__('play-video-title')}
                          onClick={() => handleActiveVideoChange(index)}
                        >
                          <svg viewBox='0 0 200 200'>
                            <use href={index === activeVideo ? iconStop : iconPlay}></use>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <h4 className={bem('s-team-opinions', 'slide-content')}>
                      <span className={bem('s-team-opinions', 'slide-name')}>{opinion.name}</span>
                      <span className={bem('s-team-opinions', 'slide-position')}>
                        {opinion.position}
                      </span>
                    </h4>
                  </div>
                ))}
              </div>
            </div>
          </div>
    </div>
  );
};

TeamOpinions.propTypes = {
  title: PropTypes.string,
  opinions: PropTypes.array
};

export default TeamOpinions;
